import React, { useState, useEffect, useMemo, useRef } from 'react';
import { oggiClinica, meseDi } from '../utils/oraItaliana';
import { spettanzeAPI, mediciAPI } from '../services/api';
import '../styles/Spettanze.css';

const Spettanze = () => {
  // Default: mese corrente, della clinica (meseDi: vedi utils/oraItaliana)
  const { dal: firstDay, al: lastDay } = meseDi();

  const [dal, setDal] = useState(firstDay);
  const [al, setAl] = useState(lastDay);
  const [medicoId, setMedicoId] = useState('');
  const [medici, setMedici] = useState([]);

  const [riepilogo, setRiepilogo] = useState(null);
  /* Il dettaglio di un medico si chiede quando lo si apre, solo per lui:
     chiave `medico|dal|al` -> { righe, completo } oppure { errore }.
     Fino al 6/10/2026 la schermata leggeva `data.visite` - la forma di
     prima - mentre la rotta risponde `data.righe` da quando le spettanze
     vengono da spettanze_maturate: il dettaglio diceva sempre «Nessuna
     visita nel periodo», per chiunque. E lo chiedeva per tutti i medici
     insieme, tagliato dal server alle prime 500 righe: in un mese pieno
     un medico poteva restare fuori dal taglio in silenzio. */
  const [dettagli, setDettagli] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedMedico, setExpandedMedico] = useState(null);

  // Carica lista medici per il filtro
  useEffect(() => {
    const loadMedici = async () => {
      try {
        const response = await mediciAPI.getAll(true);
        if (response.data.success) {
          setMedici(response.data.data.medici || []);
        }
      } catch (err) {
        console.error('Errore caricamento medici:', err);
      }
    };
    loadMedici();
  }, []);

  // Carica dati al cambio dei filtri
  useEffect(() => {
    loadData();
  }, [dal, al, medicoId]);

  /* Le risposte arrivano nell'ordine che vogliono: cambiando «Dal» e «Al»
     di seguito partono due richieste, e quella del periodo vecchio poteva
     arrivare dopo e restare a schermo. Vale solo l'ultima chiesta
     (revisione del beta, 6/10/2026). */
  const ultimaRichiesta = useRef(0);
  /* La stessa regola per il dettaglio di un medico (revisione del beta su
     b636755, 6/10/2026): un dettaglio chiesto prima di «Aggiorna» e
     arrivato dopo sovrascriveva quello riletto, con una riga in meno, 3
     volte su 3. Ogni richiesta ha un numero che non si ripete; per ogni
     chiave vale la risposta dell'ultima chiesta, e «Aggiorna» o un filtro
     cambiato scordano tutte quelle ancora in viaggio. */
  const contatoreDettagli = useRef(0);
  const ultimoDettaglio = useRef({});

  const loadData = async () => {
    const questa = ++ultimaRichiesta.current;
    try {
      setLoading(true);
      setError(null);
      /* «Aggiorna» e un filtro cambiato rileggono anche i dettagli: quelli
         gia' aperti in memoria potrebbero non essere piu' veri. */
      setDettagli({});
      ultimoDettaglio.current = {};
      const params = { dal, al };
      if (medicoId) params.medico_id = medicoId;

      const riepResp = await spettanzeAPI.getRiepilogo(params);
      if (questa !== ultimaRichiesta.current) return;
      if (riepResp.data.success) setRiepilogo(riepResp.data.data);
      if (expandedMedico) caricaDettaglio(expandedMedico);
    } catch (err) {
      if (questa !== ultimaRichiesta.current) return;
      console.error('Errore caricamento spettanze:', err);
      setError('Errore nel caricamento delle spettanze');
    } finally {
      if (questa === ultimaRichiesta.current) setLoading(false);
    }
  };

  // Scorciatoie periodi
  /* Fino al 7/10/2026 erano new Date(anno, mese, 1).toISOString(): su una
     postazione in Italia il giorno prima, e il mese andava dal 30 al 30. */
  const setMeseCorrente = () => {
    const m = meseDi();
    setDal(m.dal);
    setAl(m.al);
  };

  const setMesePrecedente = () => {
    const m = meseDi(oggiClinica(), -1);
    setDal(m.dal);
    setAl(m.al);
  };

  const setAnnoCorrente = () => {
    const anno = oggiClinica().slice(0, 4);
    setDal(`${anno}-01-01`);
    setAl(`${anno}-12-31`);
  };

  const formatEuro = (v) => {
    const n = parseFloat(v);
    if (isNaN(n)) return '€ 0,00';
    return n.toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });
  };

  const formatDate = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('it-IT');
  };

  const chiaveDettaglio = (mid) => `${mid}|${dal}|${al}`;

  const caricaDettaglio = async (mid) => {
    const chiave = chiaveDettaglio(mid);
    const questa = ++contatoreDettagli.current;
    ultimoDettaglio.current[chiave] = questa;
    try {
      const r = await spettanzeAPI.getDettaglio({ dal, al, medico_id: mid });
      if (ultimoDettaglio.current[chiave] !== questa) return;
      if (r.data.success) {
        setDettagli((d) => ({ ...d, [chiave]: {
          righe: r.data.data.righe || [],
          completo: r.data.data.completo !== false,
        } }));
      }
    } catch (err) {
      if (ultimoDettaglio.current[chiave] !== questa) return;
      setDettagli((d) => ({ ...d, [chiave]: { errore: true } }));
    }
  };

  const toggleExpand = (mid) => {
    if (expandedMedico !== mid && !dettagli[chiaveDettaglio(mid)]) caricaDettaglio(mid);
    setExpandedMedico((prev) => (prev === mid ? null : mid));
  };

  const exportCSV = () => {
    if (!riepilogo || !riepilogo.medici || riepilogo.medici.length === 0) return;

    const header = [
      'Medico',
      'Specializzazione',
      'N° Visite',
      'N° Prestazioni',
      'Imponibile',
      'Costi prodotto',
      'Margine',
      '% Spettanza',
      'Spettanza',
      'Ritenuta',
      'Netto',
    ];

    const rows = riepilogo.medici.map((m) => [
      `Dr. ${m.cognome} ${m.nome}`,
      m.specializzazione || '',
      m.prestazioni,
      m.prestazioni,
      parseFloat(m.imponibile).toFixed(2).replace('.', ','),
      parseFloat(m.costi_prodotto).toFixed(2).replace('.', ','),
      parseFloat(m.margine).toFixed(2).replace('.', ','),
      parseFloat(m.percentuale_spettanza).toFixed(2).replace('.', ','),
      parseFloat(m.spettanza).toFixed(2).replace('.', ','),
      parseFloat(m.ritenuta || 0).toFixed(2).replace('.', ','),
      parseFloat(m.netto || 0).toFixed(2).replace('.', ','),
    ]);

    const csv = [header, ...rows]
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(';'))
      .join('\n');

    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `spettanze_${dal}_${al}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  return (
    <div className="spettanze-container">
      <div className="spettanze-header">
        <h2>Calcolo spettanze</h2>
        <button
          className="btn-primary"
          onClick={exportCSV}
          disabled={!riepilogo || !riepilogo.medici || riepilogo.medici.length === 0}
        >
          Esporta CSV
        </button>
      </div>

      {/* Filtri */}
      <div className="spettanze-filtri" data-non-trattiene>
        <div className="filtri-row">
          <div className="filtro-group">
            <label>Dal</label>
            <input type="date" value={dal} onChange={(e) => setDal(e.target.value)} />
          </div>
          <div className="filtro-group">
            <label>Al</label>
            <input type="date" value={al} onChange={(e) => setAl(e.target.value)} />
          </div>
          <div className="filtro-group flex-grow">
            <label>Medico</label>
            {riepilogo?.soloProprie ? (
              /* Un medico vede soltanto le proprie: mostrargli un elenco
                 da cui scegliere sarebbe una promessa che il server non
                 mantiene. */
              <p className="sp-solo-proprie">Le tue prestazioni</p>
            ) : (
            <select value={medicoId} onChange={(e) => setMedicoId(e.target.value)}>
              <option value="">Tutti i medici</option>
              {medici.map((m) => (
                <option key={m.id} value={m.id}>
                  Dr. {m.cognome} {m.nome}
                  {m.specializzazione ? ` — ${m.specializzazione}` : ''}
                </option>
              ))}
            </select>
            )}
          </div>
        </div>
        <div className="filtri-shortcuts">
          <button type="button" onClick={setMeseCorrente}>Mese corrente</button>
          <button type="button" onClick={setMesePrecedente}>Mese precedente</button>
          <button type="button" onClick={setAnnoCorrente}>Anno corrente</button>
          <button type="button" onClick={loadData}>Aggiorna</button>
        </div>
      </div>

      {loading && <div className="spettanze-loading">Calcolo in corso...</div>}
      {error && <div className="error-message">{error}</div>}

      {!loading && riepilogo && (
        <>
          {/* Totali generali */}
          <div className="totali-card">
            <div className="totali-item">
              <span className="label">Visite</span>
              <span className="value">{riepilogo.totali.num_visite}</span>
            </div>
            <div className="totali-item">
              <span className="label">Imponibile</span>
              <span className="value">{formatEuro(riepilogo.totali.imponibile)}</span>
            </div>
            <div className="totali-item">
              <span className="label">Costi prodotto</span>
              <span className="value cost">{formatEuro(riepilogo.totali.costi_prodotto)}</span>
            </div>
            <div className="totali-item">
              <span className="label">Margine</span>
              <span className="value">{formatEuro(riepilogo.totali.margine)}</span>
            </div>
            <div className="totali-item highlight">
              <span className="label">Totale spettanze</span>
              <span className="value">{formatEuro(riepilogo.totali.spettanza)}</span>
              {riepilogo.totali.righe_da_completare > 0 && (
                <span className="senza-percentuale" title="Costo del prodotto da completare: la spettanza non è nel totale finché il lotto non ha il suo costo.">
                  {riepilogo.totali.righe_da_completare} righe da completare
                </span>
              )}
            </div>
            {/* Ritenuta e addebito compaiono solo se ci sono: un riquadro
                a zero non dice nulla e occupa spazio. */}
            {riepilogo.totali.ritenuta > 0 && (
              <div className="totali-item">
                <span className="label">
                  Ritenuta d&apos;acconto
                  {/* Su che cosa è stata calcolata lo dice il server:
                      chi legge un importo trattenuto deve poter sapere
                      su quale imponibile, e la base è un parametro che
                      può cambiare senza toccare il programma. */}
                  {riepilogo.base_ritenuta === 'lordo_meno_ai' && (
                    <em className="base-ritenuta"> sul compenso al netto dell&apos;assistenza</em>
                  )}
                </span>
                <span className="value cost">
                  -{formatEuro(riepilogo.totali.ritenuta)}
                </span>
              </div>
            )}
            {riepilogo.totali.addebito_ai > 0 && (
              <div className="totali-item">
                <span className="label">Assistenza AI</span>
                <span className="value cost">
                  -{formatEuro(riepilogo.totali.addebito_ai)}
                </span>
              </div>
            )}
            {(riepilogo.totali.ritenuta > 0 || riepilogo.totali.addebito_ai > 0) && (
              <div className="totali-item highlight">
                <span className="label">Netto da liquidare</span>
                {/* Il netto arriva dal server, calcolato in centesimi
                    interi. Rifarlo qui con tre sottrazioni in virgola
                    mobile era un quarto posto dove lo stesso numero
                    poteva uscire diverso — e a schermo, dove si legge. */}
                <span className="value">{formatEuro(riepilogo.totali.netto)}</span>
              </div>
            )}
          </div>

          {/* Tabella per medico */}
          {riepilogo.medici.length === 0 ? (
            <div className="no-results">
              Nessuna visita con medico assegnato nel periodo selezionato.
            </div>
          ) : (
            <div className="spettanze-table-wrapper rm-scorre">
              {/* data-periodo: il periodo che il server ha risposto, non
                  quello scritto nei campi. Le prove aspettano questo. */}
              <table className="spettanze-table"
                data-periodo={riepilogo.periodo ? `${riepilogo.periodo.dal}|${riepilogo.periodo.al}` : ''}>
                <thead>
                  <tr>
                    <th></th>
                    <th>Medico</th>
                    <th className="text-center">Visite</th>
                    <th className="text-center">Prestazioni</th>
                    <th className="text-right">Imponibile</th>
                    <th className="text-right">Costi</th>
                    <th className="text-right">Margine</th>
                    <th className="text-center">%</th>
                    <th className="text-right spettanza-col">Spettanza</th>
                    <th className="text-right">Ritenuta</th>
                    <th className="text-right">Netto</th>
                  </tr>
                </thead>
                <tbody>
                  {riepilogo.medici.map((m) => {
                    const det = dettagli[chiaveDettaglio(m.medico_id)];
                    const isExpanded = expandedMedico === m.medico_id;
                    return (
                      <React.Fragment key={m.medico_id}>
                        <tr className="medico-row" data-medico={m.medico_id}>
                          <td>
                            <button
                              className="btn-expand-small"
                              onClick={() => toggleExpand(m.medico_id)}
                              title={isExpanded ? 'Comprimi' : 'Espandi dettaglio'}
                            >
                              {isExpanded ? '▼' : '▶'}
                            </button>
                          </td>
                          <td>
                            <strong>Dr. {m.cognome} {m.nome}</strong>
                            {m.specializzazione && (
                              <div className="specializzazione">{m.specializzazione}</div>
                            )}
                          </td>
                          <td className="text-center">{parseFloat(m.prestazioni).toFixed(0)}</td>
                          <td className="text-center">{parseFloat(m.prestazioni).toFixed(0)}</td>
                          <td className="text-right">{formatEuro(m.imponibile)}</td>
                          <td className="text-right costo">{formatEuro(m.costi_prodotto)}</td>
                          <td className="text-right">{formatEuro(m.margine)}</td>
                          <td className="text-center">
                            {parseFloat(m.percentuale_spettanza).toFixed(0)}%
                            {/* Uno zero per percentuale mancante e uno zero
                                voluto si vedono uguali: qui si distinguono. */}
                            {m.righe_senza_percentuale > 0 && (
                              <div className="senza-percentuale"
                                   title="Righe senza percentuale di spettanza: maturano zero">
                                {m.righe_senza_percentuale} senza %
                              </div>
                            )}
                          </td>
                          <td className="text-right spettanza-col">
                            <strong>{formatEuro(m.spettanza)}</strong>
                            {/* Dal 2027 il costo del prodotto viene dal
                                magazzino: se manca, la riga non vale zero. */}
                            {m.righe_da_completare > 0 && (
                              <div className="senza-percentuale" title="Costo del prodotto da completare: la spettanza non è nel totale finché il lotto non ha il suo costo.">
                                {m.righe_da_completare} da completare
                              </div>
                            )}
                          </td>
                          <td className="text-right costo">
                            {m.ritenuta > 0 ? `-${formatEuro(m.ritenuta)}` : '—'}
                            {m.ritenuta > 0 && (
                              <div className="ritenuta-aliq">{parseFloat(m.ritenuta_aliquota).toFixed(0)}%</div>
                            )}
                          </td>
                          <td className="text-right spettanza-col">
                            <strong>{formatEuro(m.netto)}</strong>
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr className="dettaglio-row" data-medico={m.medico_id}
                            data-caricato={det ? 'si' : 'no'}
                            data-righe={det && det.righe ? det.righe.length : ''}>
                            <td colSpan="11">
                              {!det ? (
                                <div className="no-dettaglio">Caricamento del dettaglio...</div>
                              ) : det.errore ? (
                                <div className="no-dettaglio">Non riesco a leggere il dettaglio: riprova.</div>
                              ) : det.righe.length === 0 ? (
                                <div className="no-dettaglio">Nessuna riga maturata nel periodo.</div>
                              ) : (
                                <div className="dettaglio-visite">
                                  {/* Una riga per prestazione fatturata o seduta di
                                      pacchetto: e' l'allegato che il medico vede
                                      quando riceve il compenso. */}
                                  <table className="prest-detail-table spettanze-dettaglio">
                                    <thead>
                                      <tr>
                                        <th>Data</th>
                                        <th>Paziente</th>
                                        <th>Prestazione</th>
                                        <th>Documento</th>
                                        <th className="text-right">Imponibile</th>
                                        <th className="text-right">Costo</th>
                                        <th className="text-center">%</th>
                                        <th className="text-right">Spettanza</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {det.righe.map((v) => (
                                        <tr key={`${v.origine}-${v.riferimento_id}`}>
                                          <td className="rm-intero">{formatDate(v.data_maturazione)}</td>
                                          <td className="rm-tronca" title={[v.paziente_cognome, v.paziente_nome].filter(Boolean).join(' ') || v.intestatario || ''}>
                                            {[v.paziente_cognome, v.paziente_nome].filter(Boolean).join(' ') || v.intestatario || '—'}
                                          </td>
                                          <td className="rm-tronca" title={v.descrizione || ''}>{v.descrizione}</td>
                                          <td className="rm-intero">
                                            {v.origine === 'pacchetto'
                                              ? 'seduta di pacchetto'
                                              : (v.numero_fattura ? `fattura ${v.numero_fattura}` : '—')}
                                          </td>
                                          <td className="text-right">{formatEuro(v.base)}</td>
                                          <td className="text-right costo">
                                            {v.costo_prodotto === null ? '—' : formatEuro(v.costo_prodotto)}
                                          </td>
                                          <td className="text-center">
                                            {v.percentuale_spettanza === null ? '—' : `${parseFloat(v.percentuale_spettanza).toFixed(0)}%`}
                                          </td>
                                          <td className="text-right">
                                            {v.costo_da_completare
                                              ? <span className="senza-percentuale">da completare</span>
                                              : <strong>{formatEuro(v.spettanza)}</strong>}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                  {!det.completo && (
                                    <div className="no-dettaglio">
                                      Sono mostrate le prime {det.righe.length} righe: restringi il periodo per vederle tutte.
                                    </div>
                                  )}
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="formula-info">
            <strong>Formula:</strong> Spettanza = (Imponibile − Costo prodotto) × % medico, riga per riga
          </div>
        </>
      )}
    </div>
  );
};

export default Spettanze;
